import { html, LitElement, property, state } from './LitElement/index.js';
import { bound } from './decorators/index.js';
import request from './request/index.js';
import { scrollToElement } from '../scripts/helpers.js';

/**
 * Base class for orange L2 layer widgets that extends LitElement class
 */
export default class OrangeL2Widget extends LitElement {
  /**
   * @type {string}
   */
  logSource = 'generic-L2';

  /**
   * @type {string[]}
   */
  modules = [];

  /**
   * @type {URLSearchParams}
   */
  @property({ type: Object })
  accessor query = new URLSearchParams();

  /**
   * @type {?string}
   */
  @property({ type: String })
  accessor anchor = null;

  /**
   * @type {?{
   *  authManager: AuthManager,
   *  billing: Billing,
   *  consumption: Consumption,
   *  customerDataManager: CustomerDataManager,
   *  dataSharing: DataSharing,
   *  logger: Logger,
   *  loveManager: LoveManager,
   *  messageBus: MessageBus,
   *  moduleLoader: ModuleLoader,
   *  ost: Ost,
   *  products: Products,
   *  payment: Payment,
   *  router: Router,
   *  tagManager: TagManager,
   * }}
   */
  @property({ attribute: false })
  accessor infrastructure = null;

  /**
   * @type {boolean}
   */
  @state()
  accessor isLoading = true;

  /**
   * @type {?Error}
   */
  @state()
  accessor error = null;

  /**
   * @type {number}
   */
  @state()
  accessor _initialOffsetTop = 0;

  // eslint-disable-next-line class-methods-use-this
  get personalizationHandlers() {
    return {};
  }

  /**
   * Get anchor element with a default "search by ID" selector. If a more complex selector is required,
   * override this getter with a custom one. Make sure to add `?? super.anchorElement` as a fallback, as
   * navigating to an ID is generally a desired functionality.
   *
   * @return {HTMLElement}
   */
  get anchorElement() {
    return this.shadowRoot.getElementById(this.anchor);
  }

  /**
   * Default L2 data fetching method that handles setting loading and error states.
   * Can be used by providing only the `fetch` and `onSuccess`/`onFailure` method,
   * or completely overridden.
   *
   * @param {function} beforeInitialUpdate
   * @param {function} fetch
   * @param {function} onError
   * @param {function} beforeFinalUpdate
   * @param {function} afterFinalUpdate
   * @return {Promise<void>}
   */
  async getData({
    beforeInitialUpdate = () => {},
    fetch = () => {},
    onError = () => {},
    beforeFinalUpdate = () => {},
    afterFinalUpdate = () => {},
  }) {
    this.isLoading = true;
    this.error = null;
    await beforeInitialUpdate();
    await this.updateComplete;

    try {
      await fetch();
    } catch (error) {
      this.error = error;
      await onError();
    } finally {
      await beforeFinalUpdate();
      this.isLoading = false;
      await this.updateComplete;
      await afterFinalUpdate();
      this.scrollToAnchor();
    }
  }

  /**
   * Returns current offset of the `OrangeL2Widget` element from the top of the viewport.
   *
   * For pages containing megaMenu its height (represented by container offset) is reduced from the element offset.
   *
   * For CustomElements without shadowDOM there is a fallback to 0 offset.
   */
  get offsetTop() {
    if (!this.shadowRoot) {
      return 0;
    }

    const container = document.getElementById('root');
    const currentOffsetTop = this.shadowRoot.host.getBoundingClientRect().y;

    if (container) {
      return currentOffsetTop - container.offsetTop;
    }
    return currentOffsetTop;
  }

  /**
   * @param {?string} params.method - http method
   * @param {string} params.route - route without the query
   * @param {?object} params.query - query parameters as key:value object
   * @param {?object} params.headers - http headers as key:value object
   * @param {?object} params.body - object that will be processed into a string body; processing can be overridden
   * @param {?string} params.logSource - widget/module identifier for logging purposes
   * @param {?string} params.dataSource - optional identifier of the BE data source for logging purposes
   * @param {?boolean} params.useDefaultHeaders - whether to use default headers or let user specify
   * @param {?function(object): string} params.requestProcessFunction - function that processes the request body
   * @param {?function(string): object} params.responseProcessFunction - function that processes the response body
   * @param {?function(?object): boolean} params.getIsValidResponse - function that validates the processed response body
   * @param {?function(?object): boolean} params.getIsExpectedErrorResponse - function that decides if error response is expected
   * @param {?boolean} params.shouldReturnBlob - set true to avoid text processing and read response stream as blob instead; this will bypass logging (see TODO)
   * @param {?boolean} params.shouldReturnHeaders - set true to return response headers along with response body, object structure will be {response: *, headers: Headers}
   * @param {?(number[])} params.expectedErrorCodes - list of error codes that are expected as part of some user journey and will be processed
   * @param {?boolean} params.isNonessential - marks request as nonessential, downgrading its error log severity level from critical to error
   * @param {?boolean} params.isFatal - marks request as fatal if it fails, upgrading its error log severity level from critical to fatal
   * @param {?boolean} params.isImportant - marks request as important, upgrading its debug log severity from debug to info
   * @param {?boolean} params.doNotLog - mark request as one that should not be logged (mainly the log-sending one)
   * @returns {Promise<*>}
   */
  request(params) {
    return request({
      ...params,
      logSource: this.logSource,
    }).catch(this.infrastructure.logger.catchError);
  }

  /**
   * Navigation handler
   *
   * @param {String} url
   * @param {Boolean} absolute
   * @param {Boolean} replaceHistory
   */
  @bound
  navigate(url, absolute = false, replaceHistory = false) {
    this.infrastructure.router.navigate(url, absolute, replaceHistory);
  }

  /**
   * Set Query handler
   *
   * @param {URLSearchParams|string} query
   */
  @bound
  setQuery(query) {
    this.infrastructure.router.setQuery(query);
  }

  /**
   * Utility function for widgets that need to scroll the window to their top.
   */
  scrollToTop() {
    if (this.offsetTop < this._initialOffsetTop) {
      window.scrollTo(0, this._initialOffsetTop);
    }
  }

  /**
   * Scrolls the viewport onto the anchor element.
   * Will be triggered automatically at the end of `getData` invocation, after final update. Only needs to be called
   * manually when scroll navigation is desired. In such case, pass the new anchor to this method.
   *
   * Anchor element is selected via `anchorElement` getter. By default, an element with anchor as an ID is selected.
   * For more complex selection feel free to override the getter.
   *
   * Note: updating URL hash via `Navigo.navigate()` will not work due to a bug in the lib. Doing so causes strange
   * behaviour.
   *
   * @see OrangeL2Widget.anchorElement
   * @param {?string} anchor
   */
  scrollToAnchor(anchor = null) {
    if (anchor) {
      this.anchor = anchor;
    }

    if (!this.anchor) {
      return;
    }
    const element = this.anchorElement;

    if (!element) {
      return;
    }
    scrollToElement(element);
    element.setAttribute('tabindex', '-1');
    element.focus();
  }

  /**
   * @param {string} statName
   * @param {string} statGroup
   * @param {string} statCategory
   */
  logStat(statName, statGroup, statCategory) {
    this.infrastructure.logger.stat(statName, statGroup, statCategory, this.logSource);
  }

  /**
   * @param {string} message
   */
  logDebug(message) {
    return this.infrastructure.logger.debug(message, this.logSource);
  }

  /**
   * @param {string} message
   */
  logInfo(message) {
    return this.infrastructure.logger.info(message, this.logSource);
  }

  /**
   * @param {string} message
   */
  logWarning(message) {
    return this.infrastructure.logger.warning(message, this.logSource);
  }

  /**
   * @param {string} message
   */
  logError(message) {
    return this.infrastructure.logger.error(message, this.logSource);
  }

  /**
   * @param {string} message
   */
  logCritical(message) {
    return this.infrastructure.logger.critical(message, this.logSource);
  }

  /**
   * @param {string} message
   */
  logFatal(message) {
    return this.infrastructure.logger.fatal(message, this.logSource);
  }

  /**
   * Lifecycle method.
   * @param {Map<string, string>} changedProperties
   */
  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);

    this.modules.forEach(module => {
      this.infrastructure.moduleLoader.loadModule(module);
    });

    Object.keys(this.personalizationHandlers).forEach(target => {
      const handler = this.personalizationHandlers[target];
      this.infrastructure.messageBus.subscribe('personalization', target, handler);
    });
  }

  /**
   * Lifecycle method.
   *
   * Used to set `_initialOffsetTop` of the element.
   */
  connectedCallback() {
    if (super.connectedCallback) {
      super.connectedCallback();
    }
    this._initialOffsetTop = this.offsetTop;
  }

  /**
   * Lifecycle method.
   */
  disconnectedCallback() {
    Object.keys(this.personalizationHandlers).forEach(target => {
      const handler = this.personalizationHandlers[target];
      this.infrastructure.messageBus.unsubscribe('personalization', target, handler);
    });
    if (super.disconnectedCallback) {
      super.disconnectedCallback();
    }
  }

  /**
   * Errors caught in `getData` and `render` methods are delayed and thrown in the `updated` lifecycle method instead.
   * Since `updated` method is a "Post-Update" lifecycle method (see diagram in docs), delaying the error will enable
   * us to render the error screen, and throwing it here will cause `updateComplete` promise rejection which is then
   * processed by the event hook in ErrorHandler infra module.
   * @see https://lit.dev/docs/components/lifecycle/#errors-in-the-update-cycle
   * @see https://lit.dev/docs/components/lifecycle/#reactive-update-cycle
   * @param {Map<string, string>} changedProperties
   */
  updated(changedProperties) {
    super.updated(changedProperties);
    if (this.error && !this.error.wasThrown) {
      this.error.wasThrown = true;
      this.error.logSource = this.logSource;
      throw this.error;
    }
  }

  /**
   * Default L2 render implementation with load and error state handling. Can be used via `super.render()`
   * by only providing templates for various states or completely overridden with custom implementation.
   * NOTE: It's important that the templates get passed as functions, so that execution order gets decided by
   * top-most render method.
   *
   * @param {function(): TemplateResult} renderLoader
   * @param {function(): TemplateResult} renderError
   * @param {function(): TemplateResult} renderUi
   * @return {TemplateResult}
   */
  render({ renderLoader = () => html``, renderError = () => html``, renderUi = () => html`` }) {
    if (this.isLoading) {
      return renderLoader();
    }

    if (this.error) {
      return renderError();
    }

    return (() => {
      try {
        return renderUi();
      } catch (error) {
        this.error = error;
        return renderError();
      }
    })();
  }
}
